import type { FunctionalComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { IssueView } from '@/components/webzine/issue-view.tsx';
import { LoadingSpinner } from '@/components/loading-spinner.tsx';
import { useWebzineIssues } from '@/hooks/use-webzine.ts';
import type { PageProps } from '@/types/props.ts';

/**
 * 웹진 지난 호 (`/webzine/:vol`).
 *
 * 홈과 같은 레이아웃이라 `IssueView` 를 그대로 쓴다. `key` 로 호가 바뀔 때마다
 * 새로 마운트해 스크롤 위치와 인터뷰 팝업 상태를 초기화한다.
 *
 * 최신 호는 홈이 곧 그 호라 `/` 로 넘긴다. 없는 호는 목록으로 보낸다.
 */
const WebzineIssuePage: FunctionalComponent<PageProps<{ vol?: string }>> = ({ vol }) => {
  const { issues, latest, loading } = useWebzineIssues();

  const slug = (vol ?? '').toLowerCase();
  const issue = issues.find((item) => item.slug === slug) ?? null;
  const isLatest = !!issue && issue.slug === latest?.slug;

  useEffect(() => {
    if (loading) return;
    if (!issue) route('/webzine', true);
    else if (isLatest) route('/', true);
  }, [loading, issue, isLatest]);

  if (loading) return <LoadingSpinner show />;
  if (!issue || isLatest) return null;

  return (
    <IssueView
      key={issue.slug}
      issue={issue}
      backIssues={issues.filter((item) => item.slug !== issue.slug)}
      latestSlug={latest?.slug ?? null}
      tabPath="/webzine"
    />
  );
};

export default WebzineIssuePage;
